import React from 'react'
import PropTypes from 'prop-types'
import { Box, Typography, Link } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { pdsAnalytics } from './../analytics/pdsAnalytics'
import { useDynamicManageStore } from './../store/useDynamicManageStore'
import { TRADEMARK_SYMBOL } from './../constants/pdsConstant'
import phoneIcon from './../images/phone.svg'
import locationIcon from './../images/location.svg'
import acceptIcon from './../images/accept.svg'
import rejectIcon from './../images/reject.svg'

const VehicleDetails = ({ vehicleInfo, dealerName, dealerPhoneNumber }) => {
    const { t } = useTranslation()

    const {
        vehicleImage: imageUrl,
        isStatusPending,
        isStatusRejected,
        isStatusAccepted,
        getPdsStatus
    } = useDynamicManageStore((state) => state)

    return (
        <>
            <Box
                className="vehicle-details-container"
                sx={{
                    display: { smd: 'flex' },
                    gap: { smd: 4, md: 8, lmd: 12 },
                    mx: { xs: 3, md: 0 }
                }}
            >
                <Box
                    className="vehicle-img-container"
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        lineHeight: 0,
                        textAlign: 'center',
                        maxWidth: { smd: '55%', md: '50%' },
                        mb: { xs: 12, smd: 0 },
                        width: '100%'
                    }}
                >
                    <img
                        src={imageUrl}
                        alt={'car'}
                        loading="lazy"
                        width="100%"
                        height="fit-content"
                        data-testid="image"
                        className="vehicle-images"
                    />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4
                        }}
                    >
                        {!isStatusPending() && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    borderRadius: 1,
                                    backgroundColor: isStatusAccepted() ? '#E5F3E6' : '#F9E8EA',
                                    px: 4,
                                    py: 1,
                                    width: 'max-content'
                                }}
                            >
                                <>
                                    {isStatusAccepted() && (
                                        <img src={acceptIcon} alt={'accept Icon'} width={20} height={20} />
                                    )}
                                    {isStatusRejected() && (
                                        <img src={rejectIcon} alt={'reject Icon'} width={20} height={20} />
                                    )}

                                    <Typography
                                        component="div"
                                        data-testid="status"
                                        className="pds_status_label"
                                        sx={{ typography: 'mediumBody1' }}
                                    >
                                        {getPdsStatus()}
                                    </Typography>
                                </>
                            </Box>
                        )}

                        <Typography
                            component="div"
                            color={'common.charcoal.main'}
                            fontWeight={600}
                            data-testid="model"
                            sx={{ typography: 'boldTitle' }}
                        >
                            {`${vehicleInfo?.year} ${vehicleInfo?.model} ${TRADEMARK_SYMBOL}`}
                        </Typography>

                        <Typography
                            data-testid="dealer-name"
                            component="div"
                            sx={{
                                display: { xs: 'flex', smd: 'grid', md: 'flex' },
                                alignItems: 'center',
                                gap: 4,
                                flexDirection: 'row',
                                flexWrap: 'wrap'
                            }}
                        >
                            <Typography component="div" variant="boldBody1" color="common.success.main">
                                {t('ordered_from')}
                            </Typography>
                            <Typography
                                component="div"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    flexDirection: 'row',
                                    flexWrap: 'wrap'
                                }}
                            >
                                <Typography
                                    className="dealer-location"
                                    variant="div"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }}
                                >
                                    <img
                                        src={locationIcon}
                                        className="locationIcon"
                                        alt={'location Icon'}
                                        width={10}
                                        height={13}
                                    />
                                    <Typography variant="regularBody2" component="span" color="common.charcoal.main">
                                        {dealerName}
                                    </Typography>
                                </Typography>
                                {dealerPhoneNumber && (
                                    <Typography
                                        variant="regularBody2"
                                        component="div"
                                        sx={{
                                            color: 'common.charcoal.main',
                                            textTransform: 'capitalize',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                        }}
                                        className="dealer-phone"
                                    >
                                        <img
                                            src={phoneIcon}
                                            className="phoneIcon"
                                            alt={'phone Icon'}
                                            width={13}
                                            height={13}
                                        />
                                        <Typography
                                            component="div"
                                            variant="regularBody2"
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column'
                                            }}
                                            className="dealer-phone-content"
                                        >
                                            <Link
                                                href={`tel:${dealerPhoneNumber}`}
                                                onClick={() => pdsAnalytics.track('callDealer')}
                                                sx={{
                                                    color: 'common.charcoal.main',
                                                    textDecoration: 'none',
                                                    fontSize: ''
                                                }}
                                            >
                                                {dealerPhoneNumber}
                                            </Link>
                                            <Typography
                                                component="span"
                                                className="dealer-phone-border"
                                                sx={{
                                                    borderBottom: (theme) =>
                                                        `1px solid ${theme.palette.common.gray.dark}`
                                                }}
                                            ></Typography>
                                        </Typography>
                                    </Typography>
                                )}
                            </Typography>
                        </Typography>

                        <Typography
                            component="div"
                            data-testid="vin"
                            sx={{
                                color: 'common.gray.light',
                                borderTop: (theme) => `1px solid ${theme.palette.common.gray.dark}`,
                                pt: 4,
                                typography: 'regularBody2'
                            }}
                        >
                            {t('vin', { vin: vehicleInfo?.vin })}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {!isStatusAccepted() && (
                <Typography
                    component="div"
                    data-testid="review"
                    sx={{
                        color: 'common.charcoal.main',
                        textAlign: 'center',
                        typography: 'regularBody1',
                        mx: { xs: 3, md: 0 }
                    }}
                >
                    {t('sub_heading')}
                </Typography>
            )}
        </>
    )
}

VehicleDetails.propTypes = {
    vehicleInfo: PropTypes.object,
    dealerName: PropTypes.string,
    dealerPhoneNumber: PropTypes.string
}
export default VehicleDetails
